import { useMutation } from "@apollo/client";
import { clientUploadNeoFact } from "@neonumy/api-clients";
import { Card, Col, Form, FormInstance, Row, Segmented } from "antd";
import type { UploadChangeParam, UploadFile } from "antd/es/upload/interface";
import { useForm } from "antd/lib/form/Form";
import Loading from "components/Loading";
import { UPDATE_COMPANY_INVOICE_DESIGN } from "queries/neoFact/templates";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import useNotification from "utils/hooks/useNotification";
import useCoreStore from "views/hooks/useCoreStore";
import useFooterStore from "views/hooks/useFooterStore";
import invoiceStore from "views/hooks/useInvoiceStore";
import useGeneralApi from "views/Settings/General/hooks/useGeneralApi";
import useCostumizationApi from "../hooks/useCostumizationApi";
import useTemplateApi from "../hooks/useTemplateApi";
import NewTemplateSettingsForm from "./Forms/NewTemplateSettingsForm";
import SaveButtons from "./SaveButtons";
import Template1 from "./Template1";
import Template2 from "./Template2";
import Template3 from "./Template3";
import Template4 from "./Template4";

interface TemplatePropsType {
  form: FormInstance;
}

interface TemplateSettingsFormValues {
  showDocumentDescription?: boolean;
}

type TemplateUploadValue = UploadChangeParam<UploadFile> | string | null | undefined;

interface TemplateFormValues {
  template?: ID;
  logo?: TemplateUploadValue;
  backgroundImage?: TemplateUploadValue;
  logoDescription?: string;
  templateLanguage?: string;
  font?: ID;
  logoPosition?: string;
  tableBorder?: string;
  backgroundImageOpacity?: number;
  titleColor?: string;
  note?: string;
  latePaymentPenalty?: string;
  fixedRecoveryFee?: string;
  discount?: string;
  baseColor?: string;
  baseTextColor?: string;
  baseTextColorBackground?: string;
  productNameOnLine?: boolean;
}

export default function Template({ form }: TemplatePropsType): React.ReactElement {
  const { t } = useTranslation();
  const { companyId } = useParams();
  const location = useLocation();

  const tabName = location.pathname.split("/").pop();
  const { notificationError, notificationErrorDefault, notificationSuccessfullySaved } =
    useNotification();
  const [formSettings] = useForm();

  const setHandleSaveAndPreview = useFooterStore(state => state.setHandleSaveAndPreview);
  const setHandleOkSaveButton = useFooterStore(state => state.setHandleOkSaveButton);
  const setOpenPreviewModal = useFooterStore(state => state.setOpenPreviewModal);
  const handleSaveAndPreview = useFooterStore(state => state.handleSaveAndPreview);
  const handleOkSaveButton = useFooterStore(state => state.handleOkSaveButton);
  const companySettings = useCoreStore(state => state.companySettings);
  const setSaveLoading = useFooterStore(state => state.setSaveLoading);
  const saveLoading = useFooterStore(state => state.saveLoading);
  const setPreviewUrl = invoiceStore(state => state.setPreviewUrl);
  const template = Form.useWatch(["template"], form);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [removeBackgroundImage, setRemoveBackgroundImage] = useState(false);

  const { updateCompanySetting } = useGeneralApi();
  const { getCompanyWithAddress, getCompanyBankAccount, companyData, companyBankAccount } =
    useTemplateApi();
  const { templateFonts, listTemplateFonts, getCompanySaleDocumentDesign } = useCostumizationApi(
    undefined,
    form,
  );

  const newFontsOptions = templateFonts?.map(item => ({
    label: <span className={item?.label.replace(/\s+/g, "_")?.toLowerCase()}>{item?.label}</span>,
    value: item?.value,
  }));

  const [updateCompanySaleDocumentDesign] = useMutation<
    UpdateCompanySaleDocumentDesignResponse,
    UpdateCompanySaleDocumentDesignVars
  >(UPDATE_COMPANY_INVOICE_DESIGN, {
    client: clientUploadNeoFact,
  });

  useEffect(() => {
    if (handleOkSaveButton && tabName === "template") {
      handleOk();
    }
  }, [handleOkSaveButton]);

  useEffect(() => {
    if (handleSaveAndPreview) {
      handleOk();
    }
  }, [handleSaveAndPreview]);

  useEffect(() => {
    if (companyId) {
      getCompanyWithAddress({
        variables: {
          id: companyId,
          company_In: [companyId],
          limit: 2,
        },
      });
      getCompanyBankAccount({ variables: { companyId: companyId, limit: 2 } });
      getCompanySaleDocumentDesign({ variables: { companyId: companyId } });
    }
    listTemplateFonts();
  }, [companyId]);

  useEffect(() => {
    if (companySettings) {
      formSettings.setFieldsValue({
        showDocumentDescription: companySettings?.showDocumentDescription,
      });
    }
  }, [companySettings]);

  const onFinish = async (values: TemplateFormValues) => {
    const settingsValues = formSettings.getFieldsValue(true) as TemplateSettingsFormValues;

    const addLogo: Pick<UpdateCompanySaleDocumentDesignVars, "logo"> = {};
    if (!values.logo || typeof values.logo === "string") {
      delete values.logo;
    } else {
      addLogo.logo = values.logo.file.originFileObj;
    }

    const addBackgroundImage: Pick<UpdateCompanySaleDocumentDesignVars, "backgroundImage"> = {};
    if (!values.backgroundImage || typeof values.backgroundImage === "string") {
      delete values.backgroundImage;
    } else {
      addBackgroundImage.backgroundImage = values.backgroundImage.file.originFileObj;
    }
    const variables = {
      companyId: companyId,
      template: values.template,
      logoDescription: values?.logoDescription,
      templateLanguage: values?.templateLanguage,
      font: values?.font,
      logoPosition: values?.logoPosition,
      tableBorder: values?.tableBorder,
      backgroundImageOpacity: values?.backgroundImageOpacity,
      titleColor: values?.titleColor,
      note: values?.note,
      latePaymentPenalty: values?.latePaymentPenalty,
      fixedRecoveryFee: values?.fixedRecoveryFee,
      discount: values?.discount,
      baseColor: values?.baseColor,
      baseTextColor: values?.baseTextColor,
      baseTextColorBackground: values?.baseTextColorBackground,
      productNameOnLine: values?.productNameOnLine,
      removeLogo: removeLogo,
      removeBackgroundImage: removeBackgroundImage,
      ...addLogo,
      ...addBackgroundImage,
    };

    const isPreviewRequest = handleSaveAndPreview && !handleOkSaveButton;

    try {
      const response = await updateCompanySaleDocumentDesign({
        variables: variables,
      });

      const designResult = response?.data?.updateCompanySaleDocumentDesign;
      const newPreviewUrl = designResult?.companySaleDocumentDesign?.previewUrl ?? "";

      if ((designResult?.errors?.length ?? 0) > 0) {
        notificationError(designResult?.errors);
        if (isPreviewRequest) {
          notificationErrorDefault(t("Document template"));
        }
      } else {
        setPreviewUrl(newPreviewUrl);
        notificationSuccessfullySaved(t("Document template"));
        if (isPreviewRequest) {
          if (newPreviewUrl) {
            setOpenPreviewModal(true);
          } else {
            notificationErrorDefault(t("Document template"));
          }
        }
      }

      updateCompanySetting({
        variables: {
          input: {
            id: companyId,
            showDocumentDescription: settingsValues?.showDocumentDescription,
            saleDocumentTypes: companySettings?.saleDocumentTypes?.map(item => item.value),
            defaultPaymentMethods: companySettings?.defaultPaymentMethods?.map(item => item.id),
            unitMeasures: companySettings?.unitMeasures?.map(item => item.id),
          },
        },
      });
    } catch {
      if (isPreviewRequest) {
        notificationErrorDefault(t("Document template"));
      }
    } finally {
      if (isPreviewRequest) {
        setHandleSaveAndPreview(false);
      }
      setHandleOkSaveButton(false);
      setSaveLoading(false);
    }
  };
  const onFinishFailed = ({
    errorFields,
  }: {
    errorFields: { name: (string | number)[]; errors: string[] }[];
  }) => {
    if (errorFields) {
      setHandleOkSaveButton(false);
      setSaveLoading(false);
      setHandleSaveAndPreview(false);
    }
  };

  const handleOk = () => {
    form.submit();
    setSaveLoading(true);
  };

  const handleDeleteLogo = () => {
    setRemoveLogo(true);
    form.setFieldValue("logo", null);
  };

  const handleDeleteBackgroundImage = () => {
    setRemoveBackgroundImage(true);
    form.setFieldValue("backgroundImage", null);
  };

  const renderTemplate = () => {
    const safeCompanyData = companyData as {
      company: GetCompanyWithAddressCompany;
      address: AddressResult;
    };
    const safeBankAccount = companyBankAccount as BankAccountResult;

    switch (template) {
      case "1":
        return (
          <Template1 form={form} newFontsOptions={newFontsOptions} companyData={safeCompanyData} />
        );
      case "2":
        return (
          <Template2 form={form} newFontsOptions={newFontsOptions} companyData={safeCompanyData} />
        );
      case "3":
        return (
          <Template3
            form={form}
            newFontsOptions={newFontsOptions}
            companyData={safeCompanyData}
            companyBankAccount={safeBankAccount}
          />
        );
      case "4":
        return (
          <Template4 form={form} newFontsOptions={newFontsOptions} companyData={safeCompanyData} />
        );

      default:
        return (
          <Template1 form={form} newFontsOptions={newFontsOptions} companyData={safeCompanyData} />
        );
    }
  };

  return (
    <>
      {saveLoading && <Loading />}
      <Form
        id="customTemplate"
        layout="vertical"
        autoComplete="off"
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Card className="antd-newrai-card no-border-top-left">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={24}>
                  <Form.Item label={t("Template")} name="template">
                    <Segmented
                      size="small"
                      className="modern-segmented-control"
                      options={[
                        {
                          label: (
                            <div className="segmented-option-content">
                              <span className="segmented-option-text">{t("Template 1")}</span>
                            </div>
                          ),
                          value: "1",
                        },
                        {
                          label: (
                            <div className="segmented-option-content">
                              <span className="segmented-option-text">{t("Template 2")}</span>
                            </div>
                          ),
                          value: "2",
                        },
                        {
                          label: (
                            <div className="segmented-option-content">
                              <span className="segmented-option-text">{t("Template 3")}</span>
                            </div>
                          ),
                          value: "3",
                        },
                        {
                          label: (
                            <div className="segmented-option-content">
                              <span className="segmented-option-text">{t("Template 4")}</span>
                            </div>
                          ),
                          value: "4",
                        },
                      ]}
                    />
                  </Form.Item>
                </Col>
                <NewTemplateSettingsForm
                  newFontsOptions={newFontsOptions}
                  form={formSettings}
                  onDeleteLogo={handleDeleteLogo}
                  onDeleteBackgroundImage={handleDeleteBackgroundImage}
                />
              </Row>
              <SaveButtons />
            </Card>
          </Col>
          <Col xs={24} sm={12} style={{ margin: "0 auto" }}>
            <div
              style={{
                transform: "scale(0.8)",
                transformOrigin: "top center",
                overflow: "hidden",
                border: "1px solid #d9d9d9",
                width: "210mm",
                borderRadius: "8px",
              }}
            >
              {renderTemplate()}
            </div>
          </Col>
        </Row>
      </Form>
    </>
  );
}
